import {
  ConsentDocumentAuditEvent,
  ConsentDocumentAuditEventType,
  ConsentDocument,
  ConsentDocumentQuery,
  ConsentDocumentSignatory,
  ConsentDocumentStatus,
  ConsentDocumentActiveStage,
  ConsentSortOrder,
  ConsentCategory,
  ConsentSignatoryState,
  ConsentSignerRole,
  ConsentCategorySource,
  ConsentCategoryStatus,
  ConsentDocumentCategorySnapshot,
  ConsentInputValues,
  ConsentTemplate,
  CreateConsentDocumentRequest,
  ConsentTemplateQuery,
  ConsentCategoryQuery,
  EMPLOYEE_CONSENT_STATEMENT,
  EmployeeSignRequest,
  RESIDENT_CONSENT_STATEMENT,
  ResidentConsentRequest,
  ResidentIdentityVerificationRequest,
} from "./consentModels";

export const CONSENT_LIST_PAGE_SIZE = 20;

const roleLabels: Record<ConsentSignerRole, string> = {
  [ConsentSignerRole.EMPLOYEE]: "Employees",
  [ConsentSignerRole.FAMILY_MEMBER]: "Family",
  [ConsentSignerRole.RESIDENT]: "Resident",
};

export type ConsentStatusTone = "neutral" | "info" | "success" | "danger";

export interface ConsentStatusPresentation {
  label: string;
  tone: ConsentStatusTone;
}

export const getConsentStatusPresentation = (
  status: ConsentDocumentStatus,
  activeStages?: ConsentDocumentActiveStage[],
): ConsentStatusPresentation => {
  if (status === ConsentDocumentStatus.DRAFT) return {label: "Draft", tone: "neutral"};
  if (status === ConsentDocumentStatus.COMPLETED) return {label: "Completed", tone: "success"};
  if (status === ConsentDocumentStatus.DECLINED) return {label: "Declined", tone: "danger"};
  if (!Array.isArray(activeStages)) {
    return {label: "Awaiting signatures", tone: "info"};
  }
  if (activeStages.length === 0) {
    return {label: "Finalizing", tone: "info"};
  }

  const stageLabels: Record<ConsentDocumentActiveStage, string> = {
    [ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES]: roleLabels[ConsentSignerRole.EMPLOYEE],
    [ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT]: roleLabels[ConsentSignerRole.FAMILY_MEMBER],
    [ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION]: roleLabels[ConsentSignerRole.RESIDENT],
  };
  const labels = activeStages.map((stage) => stageLabels[stage]).filter(Boolean);
  if (labels.length === 1) {
    if (activeStages[0] === ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES) {
      return {label: "Awaiting employee signatures", tone: "info"};
    }
    if (activeStages[0] === ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT) {
      return {label: "Awaiting family signatures", tone: "info"};
    }
    return {label: "Ready for resident presentation", tone: "info"};
  }
  return {label: `Awaiting signatures: ${labels.join(", ")}`, tone: "info"};
};

export const getCurrentSignerPresentation = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories">,
  signerId: string,
): ConsentStatusPresentation => {
  const signer = document.signatories?.find((item) => item.id === signerId);
  if (
    signer?.signed &&
    document.status !== ConsentDocumentStatus.COMPLETED &&
    document.status !== ConsentDocumentStatus.DECLINED
  ) {
    return {label: "You signed · Waiting for other required signers", tone: "info"};
  }
  return getConsentStatusPresentation(document.status, document.activeStages);
};

export interface ConsentRoleProgress {
  role: ConsentSignerRole;
  label: string;
  total: number;
  signed: number;
  active: number;
  waiting: number;
}

export const getConsentRoleProgress = (
  signatories?: ConsentDocumentSignatory[],
): ConsentRoleProgress[] => {
  if (!signatories?.length) return [];

  return [
    ConsentSignerRole.EMPLOYEE,
    ConsentSignerRole.FAMILY_MEMBER,
    ConsentSignerRole.RESIDENT,
  ].reduce<ConsentRoleProgress[]>((groups, role) => {
    const roleSignatories = signatories.filter((signatory) => signatory.role === role);
    if (!roleSignatories.length) return groups;
    groups.push({
      role,
      label: roleLabels[role],
      total: roleSignatories.length,
      signed: roleSignatories.filter((signatory) => signatory.signed).length,
      active: roleSignatories.filter((signatory) => signatory.state === ConsentSignatoryState.ACTIVE).length,
      waiting: roleSignatories.filter((signatory) => signatory.state === ConsentSignatoryState.WAITING).length,
    });
    return groups;
  }, []);
};

const auditEventLabels: Partial<Record<ConsentDocumentAuditEventType, string>> = {
  [ConsentDocumentAuditEventType.CREATED]: "Document created",
  [ConsentDocumentAuditEventType.DRAFT_UPDATED]: "Draft updated",
  [ConsentDocumentAuditEventType.PDF_ATTACHED]: "PDF attached",
  [ConsentDocumentAuditEventType.SENT]: "Document sent",
  [ConsentDocumentAuditEventType.REMINDER_SENT]: "Reminder sent",
  [ConsentDocumentAuditEventType.EMPLOYEE_SIGNED]: "Employee signed",
  [ConsentDocumentAuditEventType.FAMILY_CONSENTED]: "Family consent recorded",
  [ConsentDocumentAuditEventType.FAMILY_DECLINED]: "Family declined",
  [ConsentDocumentAuditEventType.IDENTITY_VERIFIED]: "Resident identity verified",
  [ConsentDocumentAuditEventType.RESIDENT_CONSENTED]: "Resident consent recorded",
  [ConsentDocumentAuditEventType.RESIDENT_DECLINED]: "Resident declined",
  [ConsentDocumentAuditEventType.COMPLETED]: "Document completed",
  [ConsentDocumentAuditEventType.FINAL_PDF_VIEWED]: "Final PDF viewed",
};

/** Produces readable evidence text while allowing newly-added backend events through. */
export const getConsentAuditEventLabel = (event: Pick<ConsentDocumentAuditEvent, "type">): string =>
  auditEventLabels[event.type] || event.type.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

export const getConsentSignerRoleLabel = (role?: ConsentSignerRole): string => {
  if (role === ConsentSignerRole.EMPLOYEE) return "Employee";
  if (role === ConsentSignerRole.FAMILY_MEMBER) return "Family member";
  if (role === ConsentSignerRole.RESIDENT) return "Resident";
  return "Signer";
};

export const createEmployeeSignRequest = (): EmployeeSignRequest => ({
  consentConfirmed: true,
  acceptanceStatement: EMPLOYEE_CONSENT_STATEMENT,
});

export const employeeSignPath = (documentId: string) =>
  `/home/consent-documents/sign/${documentId}`;

export const consentTemplateBuilderPath = (templateId: string) =>
  `/home/consent-documents/templates/${templateId}`;

export const createResidentConsentRequest = (): ResidentConsentRequest => ({
  consentConfirmed: true,
  acceptanceStatement: RESIDENT_CONSENT_STATEMENT,
});

export const createResidentIdentityRequest = (
  residentFullName: string,
): ResidentIdentityVerificationRequest => ({
  identityVerified: true,
  identityVerificationStatement:
    `I confirm I am with ${residentFullName.trim()} and have verified their identity in person.`,
});

export type ConsentListQuery = ConsentCategoryQuery | ConsentTemplateQuery | ConsentDocumentQuery;

/**
 * UI-only history selections are namespaced so lifecycle and active-stage
 * values cannot be confused when constructing the API query.
 */
export type ConsentHistoryStatusFilter =
  | "all"
  | "lifecycle:draft"
  | "lifecycle:awaiting_signatures"
  | "stage:awaiting_employee_signatures"
  | "stage:awaiting_family_consent"
  | "stage:ready_for_resident_presentation"
  | "lifecycle:completed"
  | "lifecycle:declined";

export interface ConsentHistoryFilters {
  statusFilter?: ConsentHistoryStatusFilter;
  residentId?: string;
  categoryId?: string;
  search?: string;
  dateRange?: [Date, Date] | [Date] | null;
  sort: ConsentSortOrder;
}

export const getConsentDocumentHistoryStatusQuery = (
  statusFilter?: ConsentHistoryStatusFilter,
): Pick<ConsentDocumentQuery, "status" | "activeStage"> => {
  switch (statusFilter) {
    case "lifecycle:draft":
      return {status: ConsentDocumentStatus.DRAFT};
    case "lifecycle:awaiting_signatures":
      return {status: ConsentDocumentStatus.AWAITING_SIGNATURES};
    case "stage:awaiting_employee_signatures":
      return {
        status: ConsentDocumentStatus.AWAITING_SIGNATURES,
        activeStage: ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES,
      };
    case "stage:awaiting_family_consent":
      return {
        status: ConsentDocumentStatus.AWAITING_SIGNATURES,
        activeStage: ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT,
      };
    case "stage:ready_for_resident_presentation":
      return {
        status: ConsentDocumentStatus.AWAITING_SIGNATURES,
        activeStage: ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION,
      };
    case "lifecycle:completed":
      return {status: ConsentDocumentStatus.COMPLETED};
    case "lifecycle:declined":
      return {status: ConsentDocumentStatus.DECLINED};
    case "all":
    default:
      return {};
  }
};

/** Builds the list request without sending empty or unsupported filter values. */
export const createConsentDocumentHistoryQuery = ({
  statusFilter,
  residentId,
  categoryId,
  search,
  dateRange,
  sort,
}: ConsentHistoryFilters): ConsentDocumentQuery => ({
  ...getConsentDocumentHistoryStatusQuery(statusFilter),
  ...(residentId ? {residentId} : {}),
  ...(categoryId ? {categoryId} : {}),
  ...(search?.trim() ? {search: search.trim()} : {}),
  ...(dateRange?.[0] ? {startDate: new Date(new Date(dateRange[0]).setHours(0, 0, 0, 0)).toISOString()} : {}),
  ...(dateRange?.[1] ? {endDate: new Date(new Date(dateRange[1]).setHours(23, 59, 59, 999)).toISOString()} : {}),
  sort,
});

export const consentDocumentDetailPath = (documentId: string) =>
  `/home/consent-documents/documents/${documentId}`;

/** Formats the populated `createdBy` sender returned on consent list/detail DTOs. */
export const formatConsentSender = (sender?: Pick<ConsentDocument["createdBy"], "firstName" | "lastName" | "account">) => {
  const name = [sender?.firstName, sender?.lastName].filter(Boolean).join(" ").trim();
  const position = sender?.account?.position?.trim();
  if (!name) return "";
  return `Sent by ${name}${position ? ` (${position})` : ""}`;
};

export const consentDocumentEditPath = (residentId: string, documentId: string) =>
  `/home/residents/${residentId}/consent-documents/${documentId}/edit`;

export const residentPresentationPath = (residentId: string, documentId: string) =>
  `/home/residents/${residentId}/consent-documents/${documentId}/presentation`;

/** Whether the resident can be presented this document at its current server-computed stage. */
export const canPresentResidentNow = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "categorySnapshot" | "signatories" | "signatures">,
) => {
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION)
  ) return false;
  const residentAlreadySigned = document.signatures?.some((signature) =>
    signature.signerType === ConsentSignerRole.RESIDENT,
  );
  if (document.signatories !== undefined) {
    const residentSignatory = document.signatories.find((signatory) =>
      signatory.role === ConsentSignerRole.RESIDENT,
    );
    return Boolean(residentSignatory
        && !residentSignatory.signed
        && residentSignatory.state === ConsentSignatoryState.ACTIVE);
  }
  // Category card/list records are expected to include computed signatories;
  // only category-less legacy records may use this evidence-only fallback.
  return !document.categorySnapshot && !residentAlreadySigned;
};

/**
 * Validates the small amount of stage data a resident-presentation response may
 * expose. This is only for the dedicated resident-presentation endpoint: its
 * successful response is the server's authorization that presentation is
 * allowed, even when the serializer omits computed signatories.
 */
export const canUseResidentPresentationResponse = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories" | "signatures">,
) => {
  const residentAlreadySigned = document.signatures?.some((signature) =>
    signature.signerType === ConsentSignerRole.RESIDENT,
  );
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION)
  ) {
    return false;
  }
  if (document.signatories !== undefined) {
    const residentSignatory = document.signatories.find((signatory) =>
      signatory.role === ConsentSignerRole.RESIDENT,
    );
    return Boolean(
      residentSignatory
      && !residentSignatory.signed
      && residentSignatory.state === ConsentSignatoryState.ACTIVE,
    );
  }
  return !residentAlreadySigned;
};

/** Only the returned send response determines whether resident presentation is active. */
export const isResidentImmediatelyActiveAfterSend = (
  document: Pick<ConsentDocument, "status" | "activeStages">,
) => document.status === ConsentDocumentStatus.AWAITING_SIGNATURES
  && Array.isArray(document.activeStages)
  && document.activeStages.includes(ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION);

export const getResidentConsentResultCopy = (status: ConsentDocumentStatus) =>
  status === ConsentDocumentStatus.COMPLETED
    ? "All required consent steps are complete."
    : "Consent was recorded and this document is waiting for the remaining required signers.";

export interface ConsentStepsValues {
  categoryId: string;
  employeeSignerIds: string[];
  familyMemberIds: string[];
  familyFieldCompleterId: string;
  employeeInputValues: ConsentInputValues;
}

export const MAX_CONSENT_PDF_BYTES = 25 * 1024 * 1024;

/** Reduces a response Content-Type to the supported preview media type. */
export const normalizeConsentPreviewContentType = (contentType?: string): string => {
  const mediaType = contentType?.split(";", 1)[0]?.trim().toLowerCase();
  if (mediaType === "text/html") return "text/html; charset=utf-8";
  if (mediaType === "application/pdf") return "application/pdf";
  return mediaType || "";
};

export const getConsentPreviewErrorDescription = (status?: number): string | undefined => {
  if (status === 403) return "You are not authorized to preview this document at the current stage.";
  if (status === 404) return "The consent document or its confirmed source is no longer available.";
  if (status === 409) return "This document cannot be previewed in its current workflow state. Completed documents use the final PDF.";
  return undefined;
};

export interface ConsentPdfFileMetadata {
  name: string;
  size: number;
  type: string;
}

/** Validates local metadata before requesting an upload target. */
export const validateConsentPdfFile = (file: ConsentPdfFileMetadata | null): string => {
  if (!file) return "Choose a PDF file.";
  if (file.size <= 0) return "The PDF file cannot be empty.";
  if (file.size > MAX_CONSENT_PDF_BYTES) return "The PDF must be 25 MiB or smaller.";
  if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
    return "Choose a PDF file.";
  }
  return "";
};

/** Validates a single local PDF selection before an upload target is requested. */
export const validateConsentPdfFiles = (files: ConsentPdfFileMetadata[]): string => {
  if (files.length !== 1) return "Choose one PDF file.";
  return validateConsentPdfFile(files[0]);
};

export const isConsentPdfHeader = (bytes: Uint8Array) =>
  bytes.length >= 5 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44
  && bytes[3] === 0x46 && bytes[4] === 0x2d;

export const hasValidConfirmedConsentPdf = (
  document: Pick<ConsentDocument, "pdf" | "categoryId" | "categorySnapshot" | "resident"> | null,
  categoryId: string,
  residentId: string,
) => Boolean(document?.pdf?.originalFileName && categoryId && residentId
  && document.resident?._id === residentId
  && (document.categoryId || document.categorySnapshot?.categoryId) === categoryId);

export const emptyConsentStepsValues = (): ConsentStepsValues => ({
  categoryId: "",
  employeeSignerIds: [],
  familyMemberIds: [],
  familyFieldCompleterId: "",
  employeeInputValues: {},
});

export const normalizeConsentIds = (ids: string[]) =>
  Array.from(new Set(ids.filter(Boolean)));

const normalizeConsentDescriptor = (value?: string) =>
  value?.trim().replace(/\s+/g, " ").toLocaleLowerCase() || "";

/** True only when a card's category adds information beyond its title. */
export const shouldDisplayConsentCategoryName = (title?: string, categoryName?: string) =>
  Boolean(categoryName) && normalizeConsentDescriptor(title) !== normalizeConsentDescriptor(categoryName);

/** Step 1 only confirms that the selected category is still an active, fetched category. */
export const validateConsentCategorySelection = (
  category: Pick<ConsentCategory, "status"> | null,
  categoryUnavailable: boolean,
): string => {
  if (category?.status === ConsentCategoryStatus.ACTIVE) return "";
  if (categoryUnavailable || category?.status === ConsentCategoryStatus.ARCHIVED) {
    return "This Consent Category is no longer active. Select another active category.";
  }
  return "Wait for active Consent Categories to finish loading.";
};

/** Omits blank text values, while preserving supported non-text input values. */
export const cleanEmployeeInputValues = (
  template: Pick<ConsentTemplate, "employeeInputs">,
  values: ConsentInputValues,
): ConsentInputValues => template.employeeInputs.reduce<ConsentInputValues>((result, input) => {
  const value = values[input.id];
  if (input.type === "text") {
    if (typeof value === "string" && value.trim()) result[input.id] = value.trim();
  } else if (value !== undefined) {
    result[input.id] = value;
  }
  return result;
}, {});

export const createConsentStepsPayload = (
  residentId: string,
  category: Pick<ConsentCategory, "_id" | "requiredSignerRoles" | "source"> | Pick<ConsentDocumentCategorySnapshot, "categoryId" | "requiredSignerRoles" | "source">,
  template: Pick<ConsentTemplate, "employeeInputs"> | null,
  values: ConsentStepsValues,
): CreateConsentDocumentRequest => {
  const categoryId = "categoryId" in category ? category.categoryId : category._id;
  const roles = category.requiredSignerRoles;
  const isTemplate = category.source === ConsentCategorySource.TEMPLATE;
  const employees = normalizeConsentIds(values.employeeSignerIds);
  const family = normalizeConsentIds(values.familyMemberIds);
  const employeeInputValues = isTemplate && template
    ? cleanEmployeeInputValues(template, values.employeeInputValues)
    : {};
  return {
    residentId,
    categoryId,
    ...(roles.includes(ConsentSignerRole.EMPLOYEE) ? {requiredEmployeeSignerIds: employees} : {}),
    ...(roles.includes(ConsentSignerRole.FAMILY_MEMBER) ? {familyMemberIds: family} : {}),
    ...(isTemplate && roles.includes(ConsentSignerRole.FAMILY_MEMBER) && family.length > 1
      && family.includes(values.familyFieldCompleterId)
      ? {familyFieldCompleterId: values.familyFieldCompleterId}
      : {}),
    ...(Object.keys(employeeInputValues).length ? {employeeInputValues} : {}),
  };
};

export const validateConsentSteps = (
  category: Pick<ConsentCategory, "requiredSignerRoles" | "source"> | Pick<ConsentDocumentCategorySnapshot, "requiredSignerRoles" | "source"> | null,
  template: Pick<ConsentTemplate, "employeeInputs"> | null,
  values: ConsentStepsValues,
  forSend: boolean,
): string => {
  if (!category) return "Select a Consent Category.";
  if (category.requiredSignerRoles.includes(ConsentSignerRole.EMPLOYEE) && !normalizeConsentIds(values.employeeSignerIds).length) {
    return "Select at least one employee signer.";
  }
  const family = normalizeConsentIds(values.familyMemberIds);
  if (category.requiredSignerRoles.includes(ConsentSignerRole.FAMILY_MEMBER) && !family.length) return "Select at least one family member signer.";
  if (category.source === ConsentCategorySource.TEMPLATE && family.length > 1
    && (!values.familyFieldCompleterId || !family.includes(values.familyFieldCompleterId))) {
    return "Select which family member completes the document fields.";
  }
  if (forSend && category.source === ConsentCategorySource.TEMPLATE
    && template?.employeeInputs.some((input) => {
      const value = values.employeeInputValues[input.id];
      return input.type === "text" && !(typeof value === "string" && value.trim());
    })) {
    return "Complete every staff text field before sending.";
  }
  return "";
};

export const withConsentPaging = <T extends ConsentListQuery>(query: T, page: number): T & {
  page: number;
  limit: typeof CONSENT_LIST_PAGE_SIZE;
} => ({
  ...query,
  page,
  limit: CONSENT_LIST_PAGE_SIZE,
});

export interface IncrementalConsentList<T> {
  items: T[];
  page: number;
  hasMore: boolean;
}

export const createIncrementalConsentList = <T>(): IncrementalConsentList<T> => ({
  items: [],
  page: 0,
  hasMore: true,
});

export const mergeIncrementalConsentPage = <T>(
  current: IncrementalConsentList<T>,
  response: T[],
  reset = false,
): IncrementalConsentList<T> => {
  const items = reset ? response : [...current.items, ...response];
  const itemIndicesById = new Map<string, number>();
  const deduplicatedItems: T[] = [];

  items.forEach((item) => {
    const id = typeof item === "object" && item !== null && "_id" in item && typeof item._id === "string"
      ? item._id
      : undefined;

    if (!id) {
      deduplicatedItems.push(item);
      return;
    }

    const existingIndex = itemIndicesById.get(id);
    if (existingIndex === undefined) {
      itemIndicesById.set(id, deduplicatedItems.length);
      deduplicatedItems.push(item);
      return;
    }

    deduplicatedItems[existingIndex] = item;
  });

  return {
    items: deduplicatedItems,
    page: reset ? 0 : current.page + 1,
    hasMore: response.length === CONSENT_LIST_PAGE_SIZE,
  };
};
